// React
import { memo } from 'react'

// Components
import { BaseTableHeader, BaseTableAction } from 'components'
import { MyDocumentTableFooter } from './MyDocumentTableFooter'

// Antd
import { Table, Button } from 'antd'
import { ColumnsType } from 'antd/es/table'

// i18n
import { useTranslation } from 'react-i18next'

// Interfaces
import { IMyDocument } from 'interfaces/back-office/personal-data/my-document'
import { ITableEventAttrs } from 'interfaces/table'
import { IMyDocumentTableProps } from './interfaces'

const SharedMyDocumentTable = memo(({ data, loading, className, onAdd, onEdit, onChange, onDownload, onDelete, onPreview, tableHeaderRenderer, tableFooterRenderer }: IMyDocumentTableProps) => {
  // Hook
  const { t } = useTranslation()

  // Common State
  const columns: ColumnsType<IMyDocument> = [
    {
      key: 'number',
      title: t('myDocument.table.number'),
      dataIndex: 'number',
    },
    {
      key: 'documentProofName',
      title: t('myDocument.table.documentProof'),
      dataIndex: 'documentProofName',
    },
    {
      key: 'description',
      title: t('myDocument.table.description'),
      dataIndex: 'description',
    },
    {
      key: 'uploadDate',
      title: t('myDocument.table.uploadDate'),
      dataIndex: 'uploadDate',
    },
    {
      key: 'poinKum',
      title: t('myDocument.table.poinKum'),
      dataIndex: 'poinKum',
    },
    {
      key: 'x',
      title: t('app.table.action'),
      dataIndex: '',
      render: (_, record) => {
        const isDownload = record.userFileBlobId !== null && record.userFileBlobId !== '' && onDownload !== undefined
        const isPreview = record.userFileBlobId !== null && record.userFileBlobId !== '' && onPreview !== undefined
        const anotherAction = [
          {
            label: isDownload ? 'myDocument.action.downloadDocument' : '',
            onClick: () => onDownload && onDownload(record.userFileBlobId!, record.userOriginalFileName!),
          },
          {
            label: isPreview ? 'app.action.previewDocument' : '',
            onClick: () => onPreview && onPreview(record.userFileBlobId!, record.userOriginalFileName!)
          },
          {
            label: onDelete ? 'myDocument.action.deleteDocument' : '',
            onClick: () => onDelete && onDelete(record)
          }
          ].filter(action => action.label)
          
          return (
          <BaseTableAction isShow={false} isEdit={onEdit !== undefined} isDelete={false} onClickEdit={() => onEdit && onEdit(record)} anotherAction={anotherAction} />
          )
      },
    },
  ]

  /**
   * @description Add new document history
   *
   * @param {boolean} value
   *
   * @return {void} void
   */
  const onAddDocumentHistory = (value: boolean): void => {
    onAdd && onAdd(value)
  }

  /**
   * @description Watch any change in table
   *
   * @param {ITableEventAttrs} event
   *
   * @return {void} void
   */
  const onChangeTable = (event: ITableEventAttrs): void => {
    onChange && onChange(event)
  }

  return (
    <>
      {/* Table Header */}
      {
        tableHeaderRenderer ? tableHeaderRenderer() : 
        <BaseTableHeader
        searchPlaceholder="myDocument.formPlaceholder.documentSearch"
        onChange={onChangeTable}
        loading={loading}
        right={
          <div className="flex justify-end">
            <Button
              type="primary"
              className="custom-button button-table-header"
              loading={loading}
              onClick={() => onAddDocumentHistory(true)}
            >
              {t('myDocument.uploadDocument')}
            </Button>
          </div>
        }
      />
      }

      {/* Table Body */}
      <Table
        onChange={(pagination: any, filters: any, sorter: any) =>
          onChangeTable({ name: 'table', value: { pagination, filters, sorter } })
        }
        columns={columns}
        dataSource={[...data.list]}
        loading={loading}
        rowKey="id"
        pagination={false}
        className={`custom-table ${className ? className : ''}`}
      />

      {/* Table Footer */}
      {
        tableFooterRenderer ? tableFooterRenderer() : <MyDocumentTableFooter onChange={onChangeTable} loading={loading} data={data}/>
      }
    </>
  )
})

export { SharedMyDocumentTable }
